import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Eye, EyeOff, KeyRound, MailCheck, RefreshCw } from "lucide-react";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import Navbar from "../components/Navbar";
import { siteConfig } from "../lib/siteConfig";

const inputClass = "w-full rounded-lg border border-slate-200 bg-white px-4 py-3 text-slate-950 [color-scheme:light] focus:border-[#2F6FA5] focus:outline-none focus:ring-2 focus:ring-[#2F6FA5]/20";

export default function ResetPassword() {
  const nav = useNavigate();
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [codeSent, setCodeSent] = useState(false);
  const [message, setMessage] = useState("");
  const [err, setErr] = useState("");
  const [sending, setSending] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const requestCode = async (event) => {
    event?.preventDefault();
    setErr("");
    setMessage("");
    setSending(true);
    try {
      const { data } = await api.post("/auth/password/forgot", { email });
      setCodeSent(true);
      setMessage(data?.message || "If that account exists, a password reset code was sent to Gmail.");
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setSending(false);
    }
  };

  const resetPassword = async (event) => {
    event.preventDefault();
    const cleanCode = code.replace(/\D/g, "");
    if (cleanCode.length !== 6) {
      setErr("Enter the 6-digit Gmail code.");
      return;
    }
    if (password.length < 6) {
      setErr("New password must be at least 6 characters.");
      return;
    }
    if (password !== confirm) {
      setErr("Passwords do not match.");
      return;
    }
    setErr("");
    setResetting(true);
    try {
      const { data } = await api.post("/auth/password/reset", {
        email,
        code: cleanCode,
        new_password: password,
      });
      login(data.token, data.user);
      nav("/dashboard", { replace: true });
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setResetting(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 text-slate-950">
      <Navbar />
      <div className="mx-auto max-w-md px-6 pb-24 pt-16">
        <div className="mb-8 flex h-14 w-14 items-center justify-center rounded-full border border-[#2F6FA5]/30 bg-[#EAF4FC] text-[#2F6FA5]">
          {codeSent ? <MailCheck className="h-7 w-7" /> : <KeyRound className="h-7 w-7" />}
        </div>
        <h1 className="font-display mb-2 text-4xl font-black tracking-tighter">Reset password</h1>
        <p className="mb-10 text-slate-600">
          Enter your {siteConfig.name} email. We will send a 6-digit Gmail code so you can set a new password.
        </p>

        {!codeSent ? (
          <form onSubmit={requestCode} className="space-y-5">
            <div>
              <label className="mb-2 block text-xs uppercase tracking-[0.2em] text-slate-500">Email</label>
              <input
                data-testid="forgot-email"
                type="email"
                required
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                className={inputClass}
              />
            </div>
            {err && <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">{err}</div>}
            <button
              data-testid="forgot-submit"
              type="submit"
              disabled={sending}
              className="w-full rounded-full bg-[#2F6FA5] py-3 font-bold text-white transition-all hover:-translate-y-0.5 hover:bg-[#4E91C8] disabled:opacity-50"
            >
              {sending ? "Sending code..." : "Send Gmail code"}
            </button>
          </form>
        ) : (
          <form onSubmit={resetPassword} className="space-y-5">
            <div className="rounded-xl border border-slate-200 bg-white p-4 text-sm text-slate-600 break-all">
              {email}
            </div>
            {message && <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-700">{message}</div>}
            <div>
              <label className="mb-2 block text-xs uppercase tracking-[0.2em] text-slate-500">Gmail code</label>
              <input
                data-testid="reset-code"
                inputMode="numeric"
                autoComplete="one-time-code"
                required
                maxLength={6}
                value={code}
                onChange={(event) => setCode(event.target.value.replace(/\D/g, "").slice(0, 6))}
                placeholder="000000"
                className="w-full rounded-lg border border-[#2F6FA5]/30 bg-white px-4 py-3 text-center text-2xl font-black tracking-[0.5em] text-slate-950 [color-scheme:light] focus:border-[#2F6FA5] focus:outline-none focus:ring-2 focus:ring-[#2F6FA5]/20"
              />
            </div>
            <div>
              <label className="mb-2 block text-xs uppercase tracking-[0.2em] text-slate-500">New password</label>
              <div className="relative">
                <input
                  data-testid="reset-new-password"
                  type={showPassword ? "text" : "password"}
                  required
                  minLength={6}
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  className={`${inputClass} pr-12`}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((value) => !value)}
                  data-testid="reset-toggle-new-password"
                  className="absolute inset-y-0 right-0 grid w-12 place-items-center rounded-r-lg text-slate-500 hover:text-[#2F6FA5]"
                  aria-label={showPassword ? "Hide password" : "Show password"}
                  title={showPassword ? "Hide password" : "Show password"}
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>
            <div>
              <label className="mb-2 block text-xs uppercase tracking-[0.2em] text-slate-500">Confirm password</label>
              <div className="relative">
                <input
                  data-testid="reset-confirm-password"
                  type={showConfirm ? "text" : "password"}
                  required
                  minLength={6}
                  value={confirm}
                  onChange={(event) => setConfirm(event.target.value)}
                  className={`${inputClass} pr-12`}
                />
                <button
                  type="button"
                  onClick={() => setShowConfirm((value) => !value)}
                  data-testid="reset-toggle-confirm-password"
                  className="absolute inset-y-0 right-0 grid w-12 place-items-center rounded-r-lg text-slate-500 hover:text-[#2F6FA5]"
                  aria-label={showConfirm ? "Hide password" : "Show password"}
                  title={showConfirm ? "Hide password" : "Show password"}
                >
                  {showConfirm ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>
            {err && <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">{err}</div>}
            <button
              data-testid="reset-submit"
              type="submit"
              disabled={resetting}
              className="w-full rounded-full bg-[#2F6FA5] py-3 font-bold text-white transition-all hover:-translate-y-0.5 hover:bg-[#4E91C8] disabled:opacity-50"
            >
              {resetting ? "Changing password..." : "Set new password"}
            </button>
            <button
              type="button"
              onClick={requestCode}
              disabled={sending}
              className="flex w-full items-center justify-center gap-2 rounded-full border border-[#2F6FA5]/40 py-3 font-bold text-[#2F6FA5] transition-all hover:bg-[#2F6FA5]/10 disabled:opacity-50"
            >
              <RefreshCw className={`h-4 w-4 ${sending ? "animate-spin" : ""}`} />
              {sending ? "Sending..." : "Resend Gmail code"}
            </button>
          </form>
        )}

        <p className="mt-8 text-center text-sm text-slate-600">
          Remembered it? <Link to="/login" className="text-[#2F6FA5] hover:underline">Sign in</Link>
        </p>
      </div>
    </div>
  );
}
